import React, { useCallback, useEffect, useMemo, useState } from "react";

import { faBullseyeArrow } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Node as ProseMirrorNode } from "prosemirror-model";
import { Decoration, EditorView } from "prosemirror-view";
import { graphql } from "relay-runtime";
import { Loader } from "semantic-ui-react";

import { billingUpgradeIconDef, Products } from "~/billing/utils";
import type { AttendeeAttrs } from "~/common/Attendees";
import { BlockActionMenu, getUser } from "~/lib/prose/blockActions";
import { makeSpace } from "~/lib/prose/commands";
import { NodeExtension } from "~/lib/prose/extension";
import AttendeesWithoutAccessModal from "~/lib/prose/nodes/keyResult/AttendeesWithoutAccessModal";
import NoAccessEmbeddedKeyResult from "~/lib/prose/nodes/keyResult/NoAccessEmbeddedKeyResult";
import { blockActionsEnabled } from "~/lib/prose/plugins/blockActions";
import { getContext } from "~/lib/prose/plugins/context";
import { promptResponseAttrs } from "~/lib/prose/plugins/prompt";
import ReactNodeView, { type ReactNodeViewProps } from "~/lib/prose/react-node-view";
import type { getPosHandler } from "~/lib/prose/types";
import { eventMatchTagsOrClasses } from "~/lib/prose/utils";
import { decodeGlobalId, useLazyLoadQuery } from "~/lib/relayUtil";
import WebsocketClient from "~/lib/websocket";
import {
	getCommentMenuItem,
	getCopyLinkMenuItem,
	getCreatorMenuItem,
	getDeleteBlockMenu,
	getReactionMenuItem,
} from "~/notes/AssignableDropdown";
import { Viewing } from "~/okrs/EditObjective";
import EditObjectiveFromId from "~/okrs/EditObjectiveFromId";
import { Visibility } from "~/okrs/Objective";
import type { EditorContextParticipant } from "~/stream/NoteCard";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import { HelpCenterLink } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";

import type { keyResultQuery as keyResultQueryType } from "~/__generated__/keyResultQuery.graphql";

import styles from "~/lib/prose/nodes/keyResult/keyResult.module.css";

const keyResultQuery = graphql`
	query keyResultQuery($id: ID!) {
		keyResult: node(id: $id) {
			... on KeyResult {
				id
				title
				metricCurrent
				metricTarget
				progress
				assigneeUser {
					id
					email
					image
					fullName
					isGuest
				}
				assigneeTeam {
					id
					title
				}
				objective {
					id
					title
					hasAccess
					visibility
					contributors {
						id
						email
					}
					contributorsTeams {
						id
						isAccountWideTeam
						members {
							id
							email
						}
					}
				}
			}
		}
	}
`;

function EmbeddedKeyResultContent({ node, view }: ReactNodeViewProps) {
	const context = getContext(view.state);
	const [fetchKey, setFetchKey] = useState<number>(0);
	// The reason why we add !context.billing here is because a couple of prose contexts doesn't have
	// Billing in them (for example history, templates and etc). And for those we are ok with rendering
	// this block regardless of access, similarly to what we do in other blocks too.
	// We should eventually add billing to those contexts too so we can handle this case for
	// this and other blocks
	const hasAccessToObjectives = !context.billing || context?.billing?.hasAccessToObjectives;
	const { keyResultId } = node.attrs;
	const data = useLazyLoadQuery<keyResultQueryType>(
		keyResultQuery,
		{ id: keyResultId },
		{ fetchKey, fetchPolicy: "store-and-network" },
	);
	const { keyResult } = data;

	const onWebsocketMessage = useCallback(() => {
		setFetchKey(n => n + 1);
	}, []);

	useEffect(() => {
		const [, id] = decodeGlobalId(keyResultId);
		const key = `key-result.${id}`;
		const socket = WebsocketClient.getInstance().getOrJoinGroup(key, {});
		socket.on("message", onWebsocketMessage);

		return () => {
			socket.off("message", onWebsocketMessage);
		};
	}, [keyResultId, onWebsocketMessage]);

	const openObjective = useCallback(() => {
		if (!keyResult || !keyResult.objective) {
			return;
		}
		__globalModalContext?.open(({ close }) => (
			// @ts-ignore
			<EditObjectiveFromId id={keyResult.objective.id} onClose={close} />
		));
	}, [keyResult]);

	const openObjectiveSettings = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			__globalModalContext?.open(({ close }) => (
				// @ts-ignore
				<EditObjectiveFromId id={keyResult.objective.id} onClose={close} initialView={Viewing.SETTINGS} />
			));
		},
		[keyResult],
	);

	const progressClass = useMemo(() => {
		if (keyResult?.progress === undefined) {
			return;
		}
		if (keyResult.progress <= 30) {
			return styles.contentRed;
		}
		if (keyResult.progress <= 60) {
			return styles.contentOrange;
		}
		return styles.contentGreen;
	}, [keyResult]);

	const attendeesWithoutAccess: Array<AttendeeAttrs> = useMemo(() => {
		const result: Array<AttendeeAttrs> = [];
		if (!keyResult?.objective || !context.participants || keyResult?.objective?.visibility === Visibility.PUBLIC) {
			return result;
		}
		context.participants.forEach((participant: AttendeeAttrs) => {
			// We shouldn't show the give access modal for guests and anonymous participants
			if (participant.isGuest || participant.isAnonymous) {
				return;
			}
			// We look for the attendee in the contributor and in the contributor teams. If we don't find it
			// We add that user to the list of users without access
			if (keyResult.objective?.contributors.find(contributor => contributor.id === participant.id)) {
				return;
			}
			if (
				keyResult.objective?.contributorsTeams.find(
					contributorTeam =>
						contributorTeam.isAccountWideTeam || contributorTeam.members.find(member => member.id === participant.id),
				)
			) {
				return;
			}
			result.push(participant);
		});
		return result;
	}, [context, keyResult]);

	const openAttendeesWithoutAccessModal = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			__globalModalContext?.open(({ close }) => (
				<AttendeesWithoutAccessModal onClose={close} attendeesWithoutAccess={attendeesWithoutAccess} />
			));
		},
		[attendeesWithoutAccess],
	);

	const showGuestUserBanner = useMemo(() => {
		if (!keyResult?.objective || !context.participants) {
			return false;
		}
		return (
			keyResult.objective.visibility !== Visibility.PUBLIC &&
			context.participants.some((participant: EditorContextParticipant) => participant.isGuest)
		);
	}, [context, keyResult]);

	const attendeesWithoutAccessText = useMemo(() => {
		if (attendeesWithoutAccess.length === 0) {
			return null;
		}
		return (
			<>
				{attendeesWithoutAccess
					.slice(0, 2)
					.map(attendee => attendee.fullName)
					.join(attendeesWithoutAccess.length > 2 ? ", " : " and ")}
				{attendeesWithoutAccess.length > 2 ? (
					<>
						{", "}
						and{" "}
						{attendeesWithoutAccess.length === 3 ? (
							attendeesWithoutAccess[2].fullName
						) : (
							<Button size="wide" variant="tertiary" onClick={openAttendeesWithoutAccessModal}>
								{attendeesWithoutAccess.length - 2} others
							</Button>
						)}
					</>
				) : (
					""
				)}
			</>
		);
	}, [attendeesWithoutAccess, openAttendeesWithoutAccessModal]);

	if (!hasAccessToObjectives) {
		return (
			<NoAccessEmbeddedKeyResult title="Objectives is an Enterprise plan feature" icon={billingUpgradeIconDef}>
				<p>
					Create and track your objectives and key results when you upgrade to our Enterprise plan.{" "}
					<HelpCenterLink articleId={Products["fellow_unlimited"].helpArticleId}>Learn more</HelpCenterLink>
				</p>
			</NoAccessEmbeddedKeyResult>
		);
	}

	if (!keyResult) {
		return (
			<NoAccessEmbeddedKeyResult title="You do not have access to this key result" icon={faBullseyeArrow}>
				<p>An objective editor can add other editors or make the objective public from the objective settings</p>
			</NoAccessEmbeddedKeyResult>
		);
	}

	return (
		<article className={styles.keyResult} role="button" onClick={openObjective}>
			<h1>
				<FontAwesomeIcon icon={faBullseyeArrow} fixedWidth />{" "}
				{keyResult.objective?.title.trim() || <i>Untitled objective</i>} /
			</h1>
			<div className={cx(styles.content, progressClass, { [styles.compact]: context.isPrivateNote })}>
				<h2>{keyResult.title?.trim() || <i>Untitled key result</i>}</h2>
				<span>
					{keyResult.metricCurrent}/{keyResult.metricTarget}
				</span>
				<span>{keyResult.progress}%</span>
				{keyResult.assigneeUser && <Avatar link={false} user={keyResult.assigneeUser} width="1.5rem" />}
				{keyResult.assigneeTeam && (
					<Avatar
						link={false}
						team={keyResult.assigneeTeam}
						width="1.5rem"
						uiAvatarOptions={{ textForColor: keyResult.assigneeTeam.title }}
					/>
				)}
			</div>
			{showGuestUserBanner && (
				<div className={styles.giveAccess}>
					<p>Guest users can only view public objectives. Make the objective public to give them access.</p>
					<Button size="wide" variant="tertiary" onClick={openObjectiveSettings}>
						Open objective settings
					</Button>
				</div>
			)}
			{keyResult.objective?.hasAccess && attendeesWithoutAccess.length > 0 && (
				<div className={styles.giveAccess}>
					<p>
						{attendeesWithoutAccessText} {attendeesWithoutAccess.length == 1 ? "does" : "do"} not have permission to
						view this key result. Add them as {attendeesWithoutAccess.length == 1 ? "an editor " : "editors "}
						or make the objective public to give them access.
					</p>
					<Button size="wide" variant="tertiary" onClick={openObjectiveSettings}>
						Open objective settings
					</Button>
				</div>
			)}
		</article>
	);
}

function EmbeddedKeyResult(props: ReactNodeViewProps) {
	const { disabled } = props.view.extensions.get("key_result")?.options ?? {};
	return (
		// The reason for this div here is that due to the asynchronous nature of EmbeddedKeyResultContent
		// with Suspense if we don't wrap our component in something we render the block actions
		// before the content view until we rerender this now fully loaded node.
		<div pmcontent="" className={styles.keyResultBlockContent}>
			{!disabled ? (
				<React.Suspense fallback={<Loader active inline />}>
					<EmbeddedKeyResultContent {...props} />
				</React.Suspense>
			) : (
				<NoAccessEmbeddedKeyResult title="You do not have permission to view this key result" icon={faBullseyeArrow}>
					<p>Key results cannot be displayed in public notes.</p>
				</NoAccessEmbeddedKeyResult>
			)}
		</div>
	);
}

class EmbeddedKeyResultView extends ReactNodeView {
	renderComponent(onRender?: () => void) {
		super.renderComponent(() => {
			onRender?.();
			this.mountBlockActions();
		});
	}

	private mountBlockActions() {
		if (!blockActionsEnabled(this.view.state)) return;
		this.dom.appendChild(this.getBlockActions().el);
	}

	private _blockActions?: BlockActionMenu;
	private getBlockActions() {
		if (this._blockActions) return this._blockActions;
		this._blockActions = new BlockActionMenu({
			node: this.node,
			view: this.view,
			menuSections: () => {
				const { node, view, getPos } = this;
				const creatorId = node.attrs.creator;
				const context = getContext(view.state);
				const creator = context?.participants.find((p: { id: string }) => p.id === creatorId);

				if (typeof getPos === "boolean") throw new Error("boolean getPos for node");
				return [
					{
						children: context.isMeetingModal
							? [getReactionMenuItem({ blockKey: node.attrs.blockId, editorView: view })]
							: !context.isPrivateNote
								? [
										getReactionMenuItem({ blockKey: node.attrs.blockId, editorView: view }),
										getCommentMenuItem({ view, blockKey: node.attrs.blockId }),
									]
								: [],
					},
					{
						children: [
							...(context?.noteId
								? [getCopyLinkMenuItem({ noteId: context.noteId, blockKey: node.attrs.blockId })]
								: []),
							getDeleteBlockMenu({
								view,
								getPos,
								text: "Delete",
							}),
						],
					},
					{ children: !context?.isPrivateNote ? [getCreatorMenuItem({ user: getUser(), creator })] : [] },
				];
			},
			triggers: [],
		});
		return this._blockActions;
	}

	getContainer() {
		const keyResultWrapper = document.createElement("div");
		keyResultWrapper.setAttribute("data-block-id", this.attrs.blockId);
		keyResultWrapper.setAttribute("pmblock", "");
		keyResultWrapper.className = styles.keyResultBlockWrapper;
		keyResultWrapper.contentEditable = "false";
		return keyResultWrapper;
	}

	getComponent() {
		return EmbeddedKeyResult;
	}

	ignoreMutation() {
		return true;
	}

	// Removes outline effect when node is selected
	selectNode() {
		return;
	}

	deselectNode() {
		return;
	}

	// This node shouldn't be selected when clicking on it. It has its own behaviour on click.
	stopEvent(event: Event) {
		const invalidTags = ["article", "button", "p", "svg", "path"];
		const invalidClasses = ["blockActions", "content", "giveAccess", "assignableDropdown"];
		return eventMatchTagsOrClasses(event, invalidTags, invalidClasses);
	}
}

export default class EmbeddedKeyResultNode extends NodeExtension {
	constructor(options: { disabled: boolean } = { disabled: false }) {
		super(options);
	}

	get name() {
		return "key_result";
	}

	view = (node: ProseMirrorNode, view: EditorView, getPos: getPosHandler, decorations: readonly Decoration[]) => {
		return new EmbeddedKeyResultView(node, view, getPos, decorations);
	};

	get node(): NodeExtension["node"] {
		return {
			group: "block",
			attrs: {
				blockId: { default: null },
				keyResultId: { default: null },
				creator: { default: null },
				...promptResponseAttrs,
			},
			defining: true,
			inline: false,
			atom: true,
			selectable: true,
			toDOM: node => [
				"div",
				{
					"data-block-type": this.name,
					"data-block-id": node.attrs.blockId,
					"data-block-keyResultId": node.attrs.keyResultId,
				},
			],
			parseDOM: [
				{
					tag: `[data-block-type="${this.name}"]`,
					getAttrs: dom => {
						if (!(dom instanceof Element)) return false;

						const requiredAttrs = {
							blockId: dom.getAttribute("data-block-id"),
							keyResultId: dom.getAttribute("data-block-keyResultId"),
						};

						// Make sure all required values are present
						if (Object.values(requiredAttrs).some(attr => !attr)) return false;

						return {
							...requiredAttrs,
						};
					},
				},
			],
		};
	}

	keys() {
		return {
			ArrowUp: makeSpace("above"),
			ArrowLeft: makeSpace("above"),
			ArrowDown: makeSpace("below"),
			ArrowRight: makeSpace("below"),
		};
	}
}
